import React from 'react';
import {MessageStatus} from '@sabre/spark-react-core/types';
import Toolbar from '../../../common/components/toolbar/Toolbar';
import {Checkbox, ProgressIndicator} from '@sabre/spark-react-core';
import {tWithHtml} from '../../../../../common/helpers/react/text/tWithHtml';
import {tAsString} from '../../../../../common/helpers/react/text/tAsString';
import {cssClasses} from '../../../../../common/helpers/browser/cssClasses';
import {useControllers} from '../../../../../common/helpers/react/hooks/useControllers';
import VerticalSeparator from '../../../common/components/separators/VerticalSeparator';
import {getAssigneeId} from '../../../../../common/model/types/assignees/getAssigneeId';
import {AddRemoveInfo} from '../groups/wizard/agents-step/AddRemoveInfo';
import {DefaultTableCheckboxModeProps} from '../../../common/components/table/DefaultTable';
import {printBreadcrumb} from '../../../common/components/breadcrumb/printBreadcrumb';
import styles from './AssigneesToolbar.module.css'
import {ButtonConfig} from '../../../../../controllers/dialog/ButtonConfig';

export type AssigneesToolbarProps = Partial<DefaultTableCheckboxModeProps> & {
    groupId?: number,
    addRemoveInfo?: AddRemoveInfo,
    buttonsConfig?: ButtonConfig[],
    labelKey?: string,
    onNavigate?: () => void
}

export default function AssigneesToolbar(props: AssigneesToolbarProps) {
    const controllers = useControllers();
    const groups = controllers.groups;
    const dialog = controllers.dialog;

    const results = groups.getAssigneesSearchResults$().useState();
    const breadcrumbs = results?.breadcrumbs;

    const data = props.groupId !== undefined
        ? groups.getAssigneesForAddAssigneeTable$(props.groupId, props.addRemoveInfo).useState()
        : groups.getAssigneesSearchResults$().useState()?.data;

    const breadcrumbsElement = breadcrumbs?.map((it, idx) => {
        return printBreadcrumb(() => {
            groups.searchForPossibleAssignees(breadcrumbs[idx]?.query).andWeAreDone();
            props.onNavigate?.();
        }, it, idx, idx == breadcrumbs.length - 1);
    });

    const breadcrumbsOrNot = data && breadcrumbsElement
        ? breadcrumbsElement
        : <ProgressIndicator
            type={'indeterminate'}
            size={'xs'}
            className={styles.BreadcrumbsLoadingSpinner}
        />;

    let content;

    if (props.checkedRows && props.setCheckedRows !== undefined) {
        const currentTotalRowsCount = data?.length ?? 0;
        const checkedRows = props.checkedRows;
        const setCheckedRows = props.setCheckedRows;

        const totalCheckedRowsCount = props.checkedRows.length;
        const currentCheckedRowsCount = (data ?? [])
            .filter(it => {
                const assigneeId = getAssigneeId(it);
                return checkedRows.includes(assigneeId);
            }).length;

        function onChange(e: React.ChangeEvent<HTMLInputElement>) {
            if (currentTotalRowsCount > 0) {
                if (data) {
                    let checked: string[];

                    const currentDataIds = [
                        ...data.map(o => getAssigneeId(o))
                    ];

                    if (e.target.checked) {
                        checked = [
                            ...checkedRows,
                            ...currentDataIds
                        ];
                    } else {
                        checked = checkedRows
                            .filter(it => !currentDataIds.includes(it));
                    }

                    setCheckedRows(checked.uniq());
                }
            } else {
                dialog.showNotification({
                    status: MessageStatus.WARNING,
                    content: tAsString('NO_ROWS_TO_SELECT')
                }).andWeAreDone();
            }
        }

        content = (
            <>
                <Checkbox
                    label={<>{tWithHtml(`${props.labelKey ?? 'X_ASSIGNEES_SELECTED'}`, {
                        count: totalCheckedRowsCount
                    })}<VerticalSeparator className={'spark-hidden--lte-sm'}/></>}
                    checked={!!data?.length && currentTotalRowsCount === currentCheckedRowsCount}
                    onChange={onChange}
                    className={cssClasses('checkboxId')}
                />
                <span className={styles.Breadcrumbs}>{breadcrumbsOrNot}</span>
            </>
        );
    } else {
        content = (<span className={styles.Breadcrumbs}>{breadcrumbsOrNot}</span>);
    }

    const toolbarContent = results
        ? content
        : `${tAsString('LOADING')}...`;

    return <Toolbar
        contentClassName={'row'}
        content={toolbarContent}
        buttons={props.buttonsConfig}
    />;
}
